import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import Button from "@/components/ui/Button";
import CloseIcon from "@/assets/img/CloseIcon";
import { Branch, Address } from "@/types/branch";
import { CartItem } from "@/types/cart";

interface TipOption {
  value: number;
  label: string;
}

interface CheckoutDetailsViewProps {
  branch: Branch | undefined;
  adjustedCart: CartItem[];
  hasServiceInCart: boolean;
  orderMode: string;
  deliveryAddress: Address;
  deliveryFee: string;
  deliveryFeeCalculated: boolean;
  tipOptions: TipOption[];
  selectedTip: number | null;
  customTip: number | null;
  discountCode: string;
  discountValue: number;
  applyDiscountLoading: boolean;
  subtotal: number;
  tipAmount: number;
  tax: number;
  serviceFee: number;
  total: number;
  points: string;
  pointsToRedeem: number;
  usePoints: boolean;
  customer: any;
  onClose: () => void;
  onEditDeliveryAddress: () => void;
  onTipSelection: (percentage: number) => void;
  onCustomTipInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDiscountCodeChange: (value: string) => void;
  onApplyDiscount: () => void;
  onUsePointsChange: () => void;
  onCommentsChange: (value: string) => void;
  onPayWithCard: () => void;
  onPayInStore: () => void;
  productPrice: (item: CartItem) => string;
}

export const CheckoutDetailsView: React.FC<CheckoutDetailsViewProps> = ({
  branch,
  adjustedCart,
  hasServiceInCart,
  orderMode,
  deliveryAddress,
  deliveryFee,
  deliveryFeeCalculated,
  tipOptions,
  selectedTip,
  customTip,
  discountCode,
  discountValue,
  applyDiscountLoading,
  subtotal,
  tipAmount,
  tax,
  serviceFee,
  total,
  points,
  pointsToRedeem,
  usePoints,
  customer,
  onClose,
  onEditDeliveryAddress,
  onTipSelection,
  onCustomTipInput,
  onDiscountCodeChange,
  onApplyDiscount,
  onUsePointsChange,
  onCommentsChange,
  onPayWithCard,
  onPayInStore,
  productPrice,
}) => {
  return (
    <div className="checkout-details px-4">
      <div className="flex justify-between items-center mb-4">
        <p className="text-base sm:text-2xl md:text-3xl font-bold ml-2">
          Checkout Details
        </p>
        <button onClick={onClose} className="text-black">
          <CloseIcon />
        </button>
      </div>
      <div className="md:flex md:flex-row md:gap-20 md:px-20 md:mt-20">
        <div className="md:flex-1">
          {!hasServiceInCart && (
            <div className="flex justify-between mb-4">
              <span>
                {orderMode === "Delivery" ? "Delivery time" : "Pickup time"}
              </span>
              <span>15-30 min(s)</span>
            </div>
          )}
          {orderMode === "Delivery" && (
            <div className="flex items-center mb-4">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="w-6 h-6 text-red-500 mr-2"
              />
              <div className="flex justify-between w-full">
                <div>
                  <p className="font-bold">Delivery Address</p>
                  <p className="cursor-pointer">
                    {`${deliveryAddress.street}, ${deliveryAddress.city}, ${deliveryAddress.province}, ${deliveryAddress.country}, ${deliveryAddress.postal_code}`}
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="cursor-pointer"
                  onClick={onEditDeliveryAddress}
                />
              </div>
            </div>
          )}
          <div className="mx-2">
            <h3 className="text-base font-bold mb-5">Your items</h3>
            {adjustedCart.map((item) => (
              <div key={item.uniqueId} className="pb-4 mb-5 border-b">
                <div className="flex flex-row justify-between w-full">
                  <div className="w-7 h-7 bg-grayCloud flex items-center justify-center">
                    <p className="text-sm font-bold">{item.quantity}</p>
                  </div>
                  <div className="w-full ml-3 flex justify-between">
                    <p className="font-bold">{item.name}</p>
                    <p className="font-bold">
                      {item.unit_price === "0.00"
                        ? `${item.quantity} x $${productPrice(item)}`
                        : `${item.quantity} x $${item.unit_price}`}
                    </p>
                  </div>
                </div>
                <div className="pl-11">
                  {Object.entries(item.selected_options).map(
                    ([optionId, option]) =>
                      option.selected && (
                        <div key={optionId}>
                          <div className="flex justify-between w-full">
                            <p className="text-sm font-normal text-grayStone">
                              {option.name} (${option.surcharge?.toFixed(2)}) x
                              {option.quantity}
                            </p>
                            <p className="text-sm font-normal text-grayStone">
                              {option.surcharge !== undefined &&
                                `$${(
                                  option.surcharge * option.quantity
                                ).toFixed(2)}`}
                            </p>
                          </div>
                        </div>
                      ),
                  )}
                </div>
                <div className="pl-11 mt-2 flex justify-between w-full">
                  <p className="text-sm font-bold">Item total</p>
                  <p className="text-sm font-bold">
                    $
                    {(item.quantity * parseFloat(productPrice(item))).toFixed(
                      2,
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold mb-2">Tips</h3>

          <div className="flex flex-wrap justify-between gap-2 mb-4">
            {tipOptions.map((tip) => (
              <button
                key={tip.value}
                className={`h-10 flex-1 rounded-lg ${
                  selectedTip === tip.value ? "bg-redCrimson" : "bg-graySteel"
                }`}
                onClick={() => onTipSelection(tip.value)}
              >
                <p className="text-base font-bold text-white">{tip.label}</p>
              </button>
            ))}
          </div>

          <div className="mb-4">
            <input
              type="number"
              className="w-full px-3 py-2 border border-graySteel text-base font-bold text-graySteel rounded-md focus:ring-0 focus:border-graySteel"
              placeholder="Custom"
              value={customTip ?? ""}
              onChange={onCustomTipInput}
            />
          </div>

          <div className="space-y-4 mb-4">
            <div className="space-y-2">
              <h3 className="text-lg font-bold">Apply Discount Code</h3>
              <input
                type="text"
                value={discountCode}
                className="w-full px-3 py-2 border border-graySteel text-base font-bold text-graySteel rounded-md focus:ring-0 focus:border-graySteel mt-1"
                onChange={(e) => onDiscountCodeChange(e.target.value)}
                placeholder="Enter Discount Code"
              />
              <div className="w-full flex justify-center md:flex-row space-y-4 md:space-y-0">
                <Button
                  variant="primary"
                  text={applyDiscountLoading ? "Applying..." : "Apply Discount"}
                  className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px]
                          text-[16px] md:text-[18px] font-bold flex items-center justify-center mt-2"
                  onClick={onApplyDiscount}
                  disabled={applyDiscountLoading}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <span className="text-base font-normal">Subtotal</span>
            <span className="text-base font-normal">
              ${subtotal.toFixed(2)}
            </span>
          </div>
          {discountValue > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-base font-normal text-redCrimson">
                Discount
              </span>
              <span className="text-base font-normal text-redCrimson">
                -${discountValue.toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between mb-2">
            <span className="text-base font-normal">Tip</span>
            <span className="text-base font-normal">
              ${tipAmount.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-base font-normal">Tax</span>
            <span className="text-base font-normal">${tax.toFixed(2)}</span>
          </div>
          {orderMode === "Delivery" && (
            <div className="flex justify-between mb-2">
              <span className="text-base font-normal">Delivery fee</span>
              <span className="text-base font-normal">
                ${parseFloat(deliveryFee).toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between mb-2">
            <span className="text-base font-normal">Service fee</span>
            <span className="text-base font-normal">
              ${serviceFee.toFixed(2)}
            </span>
          </div>

          {branch?.loyalty_program_enabled && customer && (
            <>
              <div className="bg-graySteel text-white text-lg p-2 font-bold rounded-lg mb-2 flex items-center justify-center">
                This order will earn you{" "}
                {Math.floor(
                  subtotal *
                    parseFloat(branch?.points_to_currency_multiplier || "1.00"),
                )}{" "}
                point(s)
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-base font-normal">Points Balance</span>
                <span>
                  {usePoints
                    ? Math.floor(parseFloat(points) - pointsToRedeem)
                    : points}
                </span>
              </div>
              {parseFloat(points) <
              parseFloat(branch?.min_points_to_redeem || "1") ? (
                <div className="flex flex-col text-gray-500 font-semibold text-sm rounded-md mb-4">
                  <span className="text-base font-normal">
                    (Earn{" "}
                    {parseFloat(branch?.min_points_to_redeem ?? "0.00") -
                      parseFloat(points)}{" "}
                    points more to redeem award)
                  </span>
                </div>
              ) : parseFloat(branch?.min_order_to_redeem_points ?? "0.00") >
                subtotal ? (
                <div className="flex flex-col text-gray-500 font-semibold text-sm rounded-md mb-4">
                  <span className="text-base font-normal">
                    (Order minimun ${branch?.min_order_to_redeem_points} to
                    redeem reward)
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between space-x-2 mb-2">
                    <span className="text-base font-normal">Use my points</span>
                    <input
                      type="checkbox"
                      checked={usePoints}
                      disabled={
                        parseFloat(points) <
                          parseFloat(branch?.min_points_to_redeem || "1") ||
                        parseFloat(
                          branch?.min_order_to_redeem_points ?? "0.00",
                        ) > subtotal
                      }
                      onChange={onUsePointsChange}
                      className="disabled:opacity-50 checked:text-redCrimson disabled:bg-redCrimson foucu:outline-none focus:ring-0"
                    />
                  </div>
                  {usePoints && (
                    <div className="flex justify-between mb-2">
                      <span className="text-base font-normal text-redCrimson">
                        Loyalty Rewards
                      </span>
                      <span className="text-base font-normal text-redCrimson">
                        {`-$${(
                          pointsToRedeem *
                          parseFloat(branch?.points_conversion_rate ?? "0.01")
                        ).toFixed(2)}`}
                      </span>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div className="flex justify-between mb-4">
            <span className="text-xl font-bold">Total</span>
            <span className="text-xl font-bold">${total.toFixed(2)}</span>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Comments
            </label>
            <textarea
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              rows={3}
              placeholder="Enter any additional comments or instructions"
              onChange={(e) => onCommentsChange(e.target.value)}
            />
          </div>

          <div className="flex justify-center space-y-3">
            <Button
              variant="primary"
              text="Credit/Debit Card"
              className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px]
                                      text-[16px] md:text-[18px] font-bold flex items-center justify-center"
              onClick={onPayWithCard}
            />
          </div>
          {branch?.payment_preference === "Customer's Choice" &&
            hasServiceInCart && (
              <div className="flex justify-center space-y-3 mt-3">
                <Button
                  variant="tertiary"
                  text="Pay In Store"
                  className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px]
                                      text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                  onClick={onPayInStore}
                />
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
